/**
 * Mobile Dashboard
 *
 * Main wrapper component for the mobile app layout.
 * Combines all mobile screens with bottom tab navigation.
 */

import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MobileHeader } from './MobileHeader';
import { BottomTabNav, type MobileTabId } from './BottomTabNav';
import { MobileHomePage } from './MobileHomePage';
import { MobileCreatePage } from './MobileCreatePage';
import { MobileHistoryPage } from './MobileHistoryPage';
import { MobileServicesPage } from './MobileServicesPage';
import MFAEnrollment from '../auth/MFAEnrollment';
import { useAuth } from '../../contexts/AuthContext';

interface MobileDashboardProps {
  getAuthHeaders?: () => Promise<Record<string, string>>;
}

export const MobileDashboard: React.FC<MobileDashboardProps> = ({
  getAuthHeaders,
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<MobileTabId>('home');
  const [showSettings, setShowSettings] = useState(false);
  const [showMFASetup, setShowMFASetup] = useState(false);

  // Mock stats - replace with actual API data
  const [stats] = useState({
    postsGenerated: 0,
    wordsWritten: 0,
    knowledgeDocs: 0,
  });

  // Mock recent posts - replace with actual API data
  const [recentPosts] = useState<Array<{
    id: string;
    title: string;
    date: string;
    status: 'draft' | 'published' | 'processing';
  }>>([]);

  const handleNavigate = (tab: MobileTabId | string) => {
    // MobileHomePage may pass NavigationTarget which includes SuiteTabId
    // We only handle MobileTabId here
    if (['home', 'create', 'history', 'services'].includes(tab)) {
      setActiveTab(tab as MobileTabId);
    }
  };

  const renderContent = () => {
    const pageVariants = {
      initial: { opacity: 0, x: 20 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -20 },
    };

    switch (activeTab) {
      case 'home':
        return (
          <motion.div
            key="home"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <MobileHomePage
              onNavigate={handleNavigate}
              recentPosts={recentPosts}
              stats={stats}
            />
          </motion.div>
        );

      case 'create':
        return (
          <motion.div
            key="create"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <MobileCreatePage
              getAuthHeaders={getAuthHeaders}
              onComplete={() => handleNavigate('history')}
            />
          </motion.div>
        );

      case 'history':
        return (
          <motion.div
            key="history"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <MobileHistoryPage getAuthHeaders={getAuthHeaders} />
          </motion.div>
        );

      case 'services':
        return (
          <motion.div
            key="services"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <MobileServicesPage />
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <MobileHeader
        onSettingsClick={() => setShowSettings(true)}
        onMFAClick={() => setShowMFASetup(true)}
      />

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
      </main>

      {/* Bottom Navigation */}
      <BottomTabNav activeTab={activeTab} onTabChange={setActiveTab} />

      {/* MFA Setup Modal */}
      <AnimatePresence>
        {showMFASetup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowMFASetup(false);
            }}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-h-[90vh] bg-white rounded-t-3xl overflow-y-auto pb-[env(safe-area-inset-bottom)]"
            >
              <div className="sticky top-0 bg-white px-4 py-3 border-b border-gray-100 flex items-center justify-center">
                <div className="w-12 h-1 bg-gray-300 rounded-full" />
              </div>
              <div className="p-4">
                <MFAEnrollment
                  onComplete={() => setShowMFASetup(false)}
                  onSkip={() => setShowMFASetup(false)}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings Bottom Sheet */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowSettings(false);
            }}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full bg-white rounded-t-3xl overflow-hidden pb-[env(safe-area-inset-bottom)]"
            >
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-center">
                <div className="w-12 h-1 bg-gray-300 rounded-full" />
              </div>
              <div className="p-4 space-y-4">
                <h2 className="text-xl font-bold text-gray-900">Settings</h2>

                {/* Account Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Account</h3>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg">
                      {user?.displayName?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user?.displayName}</p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Security Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Security</h3>
                  <button
                    onClick={() => {
                      setShowSettings(false);
                      setShowMFASetup(true);
                    }}
                    className="w-full flex items-center justify-between p-3 bg-white rounded-lg"
                  >
                    <span className="font-medium text-gray-900">Two-Factor Auth</span>
                    <span
                      className={`text-sm px-2 py-1 rounded-full ${
                        user?.mfaEnabled
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {user?.mfaEnabled ? 'Enabled' : 'Not Set'}
                    </span>
                  </button>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => setShowSettings(false)}
                  className="w-full py-3 px-4 bg-gray-200 text-gray-700 rounded-xl font-medium touch-target"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileDashboard;
